import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { api } from "../api/client";
import { Icon } from "../components/Icon";
import { PageHeader } from "../layout/PageHeader";
import { useToast } from "../system/ToastContext";
import { runtime } from "../api/runtime";
import { registrarOrdenOffline } from "../lib/fieldOpsRuntime";

interface OrdenInspeccionRead {
  orden_id: string;
  alerta_id: string;
  estado: string;
  resultado: string | null;
  asignada_en: string;
  cerrada_en: string | null;
  sla_dias: number;
  direccion?: string;
  cliente_codigo?: string;
  dma_codigo?: string;
}

const ESTADO_BADGE: Record<string, string> = {
  asignada: "badge badge--info",
  en_inspeccion: "badge badge--warn",
  cerrada: "badge badge--accent",
  escalada: "badge badge--danger",
};

const RESULTADOS = [
  { key: "regularizacion", label: "Regularización" },
  { key: "corte", label: "Corte" },
  { key: "normalizacion_medidor", label: "Normalización medidor" },
  { key: "sin_hallazgos", label: "Sin hallazgos" },
];

function fmtDate(iso: string | null): string {
  if (!iso) return "—";
  return iso.replace("T", " ").slice(0, 16);
}

function daysSince(iso: string): number {
  return Math.floor((Date.now() - new Date(iso).getTime()) / (1000 * 60 * 60 * 24));
}

export function InspectorOrdenes(): JSX.Element {
  const { toast } = useToast();
  const query = useQuery<OrdenInspeccionRead[]>({
    queryKey: ["inspector.ordenes"],
    queryFn: () => api<OrdenInspeccionRead[]>("/ordenes/mias"),
  });

  /** Overrides locales: estado + resultado + volumen recuperado. */
  const [overrides, setOverrides] = useState<
    Record<string, { estado: string; resultado: string; volumen?: number }>
  >({});
  const [activo, setActivo] = useState<OrdenInspeccionRead | null>(null);
  const [resultadoSel, setResultadoSel] = useState("regularizacion");
  const [volumen, setVolumen] = useState("");
  /** Identificador NFC/QR leído y referencia de evidencia asociada (R8.7–R8.9). */
  const [nfcQr, setNfcQr] = useState("");
  const [evidencia, setEvidencia] = useState<string | null>(null);

  const ordenes = useMemo(() => {
    const base = query.data ?? [];
    return base.map((o) =>
      overrides[o.orden_id]
        ? { ...o, estado: overrides[o.orden_id].estado, resultado: overrides[o.orden_id].resultado }
        : o,
    );
  }, [query.data, overrides]);

  const abiertas = ordenes.filter((o) => o.estado !== "cerrada");
  const cerradas = ordenes.filter((o) => o.estado === "cerrada");
  const enRiesgo = abiertas.filter((o) => daysSince(o.asignada_en) >= o.sla_dias);

  /** Identificador NFC/QR esperado del Medidor/punto de servicio de la orden. */
  function identificadorEsperado(o: OrdenInspeccionRead): string {
    return `MED-${o.orden_id.slice(-6)}`;
  }

  async function abrirCierre(o: OrdenInspeccionRead): Promise<void> {
    setActivo(o);
    setResultadoSel("regularizacion");
    setVolumen("");
    setNfcQr("");
    setEvidencia(null);
    // Registra la orden activa en el almacén offline de Field_Ops para
    // poder asociar evidencia vía NFC/QR sin conectividad (R8.5).
    await registrarOrdenOffline({
      orden_ref: { tipo: "Orden_Inspeccion", id: o.orden_id },
      coord: { lon: -70.4, lat: -23.65 },
      identificador: identificadorEsperado(o),
    }).catch(() => undefined);
  }

  /** Asocia evidencia NFC/QR a la orden activa vía Field_Ops (R8.7–R8.9). */
  async function asociarEvidencia(): Promise<void> {
    if (!activo) return;
    const orden_ref = { tipo: "Orden_Inspeccion" as const, id: activo.orden_id };
    const res = await runtime.fieldOps().associateEvidence(orden_ref, nfcQr.trim());
    if (res.ok) {
      setEvidencia(res.value.evidencia_ref.id);
      toast(`Evidencia asociada a la orden ${activo.orden_id.slice(-4)}`, "success");
    } else {
      // R8.8: id no coincidente → rechaza, la evidencia capturada se preserva.
      toast(res.error, "danger");
    }
  }

  function iniciarInspeccion(o: OrdenInspeccionRead): void {
    setOverrides((prev) => ({
      ...prev,
      [o.orden_id]: { estado: "en_inspeccion", resultado: o.resultado ?? "" },
    }));
    toast(`Inspección iniciada en ${o.orden_id.slice(-4)}`, "info");
  }

  function confirmarCierre(): void {
    if (!activo) return;
    setOverrides((prev) => ({
      ...prev,
      [activo.orden_id]: {
        estado: "cerrada",
        resultado: resultadoSel,
        volumen: volumen ? Number(volumen) : undefined,
      },
    }));
    toast(
      `Orden ${activo.orden_id.slice(-4)} cerrada · ${
        RESULTADOS.find((r) => r.key === resultadoSel)?.label
      }`,
      "success",
    );
    setActivo(null);
  }

  return (
    <>
      <PageHeader
        eyebrow="Inspector · R8.*"
        title="Mis Órdenes de Inspección"
        subtitle="Listado de órdenes asignadas y registro de resultados."
      />

      <div className="card-grid" style={{ marginBottom: 16 }}>
        <div className="kpi">
          <span className="kpi__label">Órdenes asignadas</span>
          <span className="kpi__value">{ordenes.length}</span>
          <span className="kpi__hint">{abiertas.length} abiertas</span>
        </div>
        <div className="kpi kpi--accent">
          <span className="kpi__label">Órdenes cerradas</span>
          <span className="kpi__value">{cerradas.length}</span>
          <span className="kpi__hint">incluyen sin_hallazgos y regularización</span>
        </div>
        <div className="kpi kpi--danger">
          <span className="kpi__label">Fuera de SLA</span>
          <span className="kpi__value">{enRiesgo.length}</span>
          <span className="kpi__hint">SLA por defecto: 10 días</span>
        </div>
      </div>

      {query.isLoading && (
        <div className="status-block status-block--info">Cargando órdenes…</div>
      )}

      {query.data && (
        <section className="card">
          <h2 className="card__title">Detalle</h2>
          <div className="table-wrapper" style={{ borderColor: "transparent" }}>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Orden</th>
                  <th>Estado</th>
                  <th>Resultado</th>
                  <th>Asignada</th>
                  <th className="numeric">Antigüedad</th>
                  <th>Acción</th>
                </tr>
              </thead>
              <tbody>
                {ordenes.map((o) => {
                  const days = daysSince(o.asignada_en);
                  const overSla = days >= o.sla_dias && o.estado !== "cerrada";
                  return (
                    <tr key={o.orden_id}>
                      <td className="text-mono">{o.orden_id.slice(-6)}</td>
                      <td>
                        <span className={ESTADO_BADGE[o.estado] ?? "badge badge--neutral"}>
                          {o.estado}
                        </span>
                      </td>
                      <td>{o.resultado ?? "—"}</td>
                      <td className="text-mono">{fmtDate(o.asignada_en)}</td>
                      <td className="numeric">
                        {days} días{" "}
                        {overSla && (
                          <span className="badge badge--danger" style={{ marginLeft: 6 }}>
                            SLA
                          </span>
                        )}
                      </td>
                      <td>
                        {o.estado === "asignada" && (
                          <button className="btn btn--ghost" onClick={() => iniciarInspeccion(o)}>
                            <Icon name="search" size={12} /> Iniciar
                          </button>
                        )}
                        {o.estado === "en_inspeccion" && (
                          <button className="btn btn--primary" onClick={() => void abrirCierre(o)}>
                            <Icon name="wrench" size={12} /> Registrar
                          </button>
                        )}
                        {o.estado === "cerrada" && (
                          <span className="text-faint" style={{ fontSize: 11 }}>
                            cerrada
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {activo && (
        <>
          <div className="drawer-overlay" onClick={() => setActivo(null)} />
          <aside className="drawer" role="dialog" aria-label="Registrar resultado">
            <div className="drawer__header">
              <div>
                <div style={{ fontWeight: 600, color: "var(--c-text-strong)" }}>
                  Registrar resultado
                </div>
                <div className="text-faint" style={{ fontSize: 11 }}>
                  Orden {activo.orden_id.slice(-6)}
                </div>
              </div>
              <button className="btn btn--ghost btn--icon" onClick={() => setActivo(null)} aria-label="Cerrar">
                <Icon name="logout" size={14} />
              </button>
            </div>
            <div className="drawer__body vstack">
              <label className="form-field">
                Resultado de la inspección
                <select value={resultadoSel} onChange={(e) => setResultadoSel(e.target.value)}>
                  {RESULTADOS.map((r) => (
                    <option key={r.key} value={r.key}>
                      {r.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="form-field">
                Volumen recuperado (m³, opcional)
                <input
                  type="number"
                  value={volumen}
                  onChange={(e) => setVolumen(e.target.value)}
                  placeholder="0"
                />
              </label>
              <label className="form-field">
                Evidencia · identificador NFC/QR del medidor
                <input
                  type="text"
                  value={nfcQr}
                  onChange={(e) => setNfcQr(e.target.value)}
                  placeholder={identificadorEsperado(activo)}
                />
              </label>
              <button
                className="btn btn--ghost"
                disabled={nfcQr.trim().length === 0}
                onClick={() => void asociarEvidencia()}
              >
                <Icon name="search" size={13} /> Asociar evidencia (NFC/QR)
              </button>
              {evidencia && (
                <div className="status-block status-block--info">
                  <Icon name="shield" size={14} />
                  Evidencia <span className="text-mono">{evidencia.slice(-8)}</span> registrada como evento (R8.9).
                </div>
              )}
              <div className="status-block">
                <Icon name="shield" size={14} />
                El cierre genera un evento append-only en el audit log.
              </div>
              <button className="btn btn--primary" onClick={confirmarCierre}>
                <Icon name="wrench" size={13} /> Confirmar cierre
              </button>
            </div>
          </aside>
        </>
      )}
    </>
  );
}
